import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";

// `subjects` is a free-text field ("Maths, Science" / "Class VI-X Science"),
// so the filter list is built from whatever teachers actually have rather
// than from a fixed taxonomy nobody maintains.
const splitSubjects = (value) =>
  String(value ?? "")
    .split(/[,/|]/)
    .map((part) => part.trim())
    .filter(Boolean);

const toExperienceYears = (value) => {
  const years = Number.parseFloat(String(value ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(years) ? years : -1;
};

// MUI's Avatar renders its children when the image fails to load, so a photo
// that 402s or 404s degrades to the teacher's initials rather than an empty
// grey disc.
const initials = (name) =>
  String(name ?? "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("") || "?";

function TeacherDirectory({ fetchTeachers, emptyText }) {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("all");
  const [sort, setSort] = useState("name");

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const rows = await fetchTeachers();
        if (active) setTeachers(Array.isArray(rows) ? rows : []);
      } catch (loadError) {
        console.error("Could not load teachers:", loadError);
        if (active) setError("We could not load the teacher list. Please refresh the page.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [fetchTeachers]);

  const subjectOptions = useMemo(() => {
    const seen = new Map();
    teachers.forEach((teacher) =>
      splitSubjects(teacher.subjects).forEach((entry) => {
        const key = entry.toLowerCase();
        if (!seen.has(key)) seen.set(key, entry);
      })
    );
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [teachers]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();

    const filtered = teachers.filter((teacher) => {
      if (subject !== "all") {
        const hasSubject = splitSubjects(teacher.subjects).some(
          (entry) => entry.toLowerCase() === subject
        );
        if (!hasSubject) return false;
      }
      if (!needle) return true;
      return [teacher.name, teacher.subjects, teacher.qualification]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle));
    });

    return filtered.sort((a, b) => {
      if (sort === "experience")
        return toExperienceYears(b.experience) - toExperienceYears(a.experience);
      return String(a.name ?? "").localeCompare(String(b.name ?? ""));
    });
  }, [teachers, search, subject, sort]);

  const resetFilters = () => {
    setSearch("");
    setSubject("all");
    setSort("name");
  };

  return (
    <Container sx={{ py: { xs: 3, md: 4 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
        sx={{ mb: 3 }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, subject or qualification"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Subject"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          sx={{ minWidth: { md: 200 } }}
        >
          <MenuItem value="all">All subjects</MenuItem>
          {subjectOptions.map((option) => (
            <MenuItem key={option} value={option.toLowerCase()}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Sort by"
          value={sort}
          onChange={(event) => setSort(event.target.value)}
          sx={{ minWidth: { md: 180 } }}
        >
          <MenuItem value="name">Name (A–Z)</MenuItem>
          <MenuItem value="experience">Most experience</MenuItem>
        </TextField>
      </Stack>

      {!loading && (
        <MKTypography variant="button" color="text" sx={{ display: "block", mb: 2 }}>
          {visible.length} {visible.length === 1 ? "teacher" : "teachers"}
          {teachers.length !== visible.length ? ` of ${teachers.length}` : ""}
        </MKTypography>
      )}

      {loading ? (
        <Grid container spacing={2.5}>
          {[0, 1, 2, 3, 4, 5].map((key) => (
            <Grid item xs={12} sm={6} lg={4} key={key}>
              <Skeleton variant="rounded" height={190} />
            </Grid>
          ))}
        </Grid>
      ) : visible.length === 0 ? (
        <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
          <MKTypography variant="body2" color="text">
            {teachers.length === 0 ? emptyText : "No teachers match those filters."}
          </MKTypography>
          {teachers.length > 0 && (
            <MKButton variant="outlined" color="primary" onClick={resetFilters}>
              Clear filters
            </MKButton>
          )}
        </Stack>
      ) : (
        // The old page rendered a raw <table style={{ minWidth: 850 }}>, which
        // simply ran off the side of every phone screen.
        <Grid container spacing={2.5}>
          {visible.map((teacher) => (
            <Grid item xs={12} sm={6} lg={4} key={teacher.id}>
              <Card
                sx={{
                  height: "100%",
                  p: 2.5,
                  borderRadius: 3,
                  border: ({ palette: { brand } }) => `1px solid ${brand.line}`,
                  boxShadow: "none",
                  transition: "transform 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: ({ boxShadows: { lg } }) => lg,
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                  <Avatar
                    src={teacher.photo}
                    alt={teacher.name}
                    sx={{
                      width: 68,
                      height: 68,
                      border: "2px solid #e3e8f0",
                      bgcolor: ({ palette: { brand } }) => brand.navy,
                      fontSize: "1.1rem",
                    }}
                  >
                    {initials(teacher.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <MKTypography variant="h5" sx={{ fontSize: "1.05rem" }}>
                      {teacher.name}
                    </MKTypography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <WorkHistoryIcon sx={{ fontSize: 15, color: "#7b809a" }} />
                      <MKTypography variant="button" color="text">
                        {teacher.experience} years experience
                      </MKTypography>
                    </Stack>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mb: 1.5 }}>
                  <SchoolIcon sx={{ fontSize: 16, color: "#7b809a", mt: 0.25 }} />
                  <MKTypography variant="button" color="text">
                    {teacher.qualification}
                  </MKTypography>
                </Stack>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {splitSubjects(teacher.subjects).map((entry) => (
                    <Chip
                      key={entry}
                      label={entry}
                      size="small"
                      sx={{
                        bgcolor: ({ palette: { brand } }) => brand.mist,
                        color: ({ palette: { brand } }) => brand.navy,
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

TeacherDirectory.defaultProps = { emptyText: "No teachers have been published yet." };

TeacherDirectory.propTypes = {
  fetchTeachers: PropTypes.func.isRequired,
  emptyText: PropTypes.string,
};

export default TeacherDirectory;
